/* global process */
import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import jwt_decode from "jwt-decode";
import { selectIdToken, selectUser } from "../slices/authSlice";
import { getExecutionDocType } from "../utils/addInUtils";
import { AuthApi } from "./auth-api";

const addonBaseQuery = fetchBaseQuery({
  baseUrl: process.env.API_BASE_URL,
});

/**
 * On 401 or a network failure, requests a new client-credentials token and retries
 * only this call. The token is not stored and is not shared with another addon call.
 */
const retryWithFreshAddonToken: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  let result = await addonBaseQuery(args, api, extraOptions);
  if (!result.error || (result.error.status !== 401 && result.error.status !== "FETCH_ERROR")) {
    return result;
  }

  try {
    const tokenResponse = await api.dispatch(AuthApi.endpoints.login.initiate()).unwrap();
    const accessToken = tokenResponse?.access_token;
    if (!accessToken) {
      return result;
    }

    const authorizedArgs: FetchArgs =
      typeof args === "string"
        ? { url: args, headers: { Authorization: `Bearer ${accessToken}` } }
        : {
            ...args,
            headers: {
              ...((args.headers as Record<string, string> | undefined) ?? {}),
              Authorization: `Bearer ${accessToken}`,
            },
          };
    return await addonBaseQuery(authorizedArgs, api, extraOptions);
  } catch (_error) {
    return result;
  }
};

export const OpportunityApi = createApi({
  reducerPath: "opportunityApi",
  baseQuery: retryWithFreshAddonToken,
  endpoints: (builder) => ({
    getOppNumbersByKeyword: builder.query({
      async queryFn(keyword, _api, _extraOptions, baseQuery) {
        return baseQuery(`addon/search?keyword=${keyword}`);
      },
    }),
    getBidById: builder.query({
      // @ts-ignore
      async queryFn(id, api, _extraOptions, baseQuery) {
        let userName = "";
        let userEmail = "";
        const systemName = getExecutionDocType();
        const state = api.getState();
        const user = selectUser(state);
        userName = user?.name || "";
        userEmail = user?.email || "";

        if (!userName || !userEmail) {
          try {
            const idToken = selectIdToken(state);
            const decoded: any = jwt_decode(idToken);
            userName = decoded["name"] || "";
            userEmail = decoded["email"] || "";
          } catch (_error) {
            // The API authorizer remains the source of truth; the optional
            // body metadata stays empty when claims cannot be decoded.
          }
        }
        return baseQuery({
          url: `addon/proposal?proposal_id=${id}`,
          method: "POST",
          body: {
            userName,
            userEmail,
            systemName,
          },
        });
      },
    }),
  }),
});
